import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { MappingType, OperationType } from '../../../../../../../models/Account'
import { useMemo } from 'react'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetIntegrationAdminSettings } from '../../../hooks'

interface SelectTaskTypeSectionProps {
  onChange: () => void
  serviceId: IntegrationServiceId
}

export const SelectTaskTypeSection: React.FC<SelectTaskTypeSectionProps> = ({ onChange, serviceId }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)
  const currentObjectName = watch('mappings.task.objectName')
  const currentMappingType = watch('mappings.task.mappingType')
  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  const taskCRMTypeOptions: SelectOption[] = useMemo(
    () => [
      { id: 'task', label: 'Task' },
      { id: 'customTaskObject', label: 'Custom Task Object' },
    ],
    [],
  )
  const predefinedTaskOption: SelectOption = useMemo(() => {
    if (isCustom) {
      return { id: 'customTaskObject', label: 'Custom Task Object' }
    }
    return taskCRMTypeOptions.find((opt) => opt.id === currentObjectName) || { id: 'task', label: 'Task' }
  }, [taskCRMTypeOptions, currentObjectName, isCustom])

  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const customTaskOption = useMemo(() => supportedObjects?.find((opt) => opt.name === currentObjectName) || null, [supportedObjects, currentObjectName])

  const customTaskOptions: SelectOption[] = useMemo(() => {
    return supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || []
  }, [supportedObjects])

  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && isCustom) {
      return
    }
    setValue('mappings.task', { objectName: option.id, operationType: OperationType.CREATE }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.task.mappingType', MappingType.CUSTOM, {
      shouldDirty: true,
    })
    onChange()
  }

  const handlePredefinedOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && !isCustom) {
      return
    }

    if (option.id === 'customTaskObject') {
      setValue('mappings.task', { objectName: null }, { shouldDirty: true, shouldValidate: true })
      setValue('mappings.task.mappingType', MappingType.CUSTOM, {
        shouldDirty: true,
        shouldValidate: true,
      })
      onChange()

      return
    }
    setValue('mappings.task', null, { shouldDirty: true, shouldValidate: true })
    onChange()
  }

  const fieldError = errors.mappings?.task?.objectName
  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>Select Object Type</h4>
          <p className='zocks-text-gray-600'>Select the {getIntegrationDisplayName(serviceId)} object where Tasks will be saved.</p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectDropdown initialSelectedOption={predefinedTaskOption} options={taskCRMTypeOptions} onOptionSelected={handlePredefinedOptionSelect} />
        </Col>
        {isCustom && (
          <Col md={6}>
            <SelectDropdown
              options={customTaskOptions}
              initialSelectedOption={customTaskOption ? { id: customTaskOption.name, label: `${customTaskOption.label} (${customTaskOption.name})` } : null}
              placeholder='Select Object Type'
              onOptionSelected={handleCustomOptionSelect}
              searchable
              loading={isLoadingSupportedObjects}
            />
            {fieldError && <div className='text-danger mt-1 small'>{fieldError.message}</div>}
          </Col>
        )}
      </Row>
    </>
  )
}
